"use client";

import { useState } from "react";
import { BadgeDollarSign, CreditCard, Gift, Handshake, Link2, ShieldCheck, Store, Users, Utensils, Wallet, Wrench } from "lucide-react";
import MarketingNav from "@/components/marketing/MarketingNav";
import MarketingFooter from "@/components/marketing/MarketingFooter";
import PartnerLeadForm from "@/components/marketing/PartnerLeadForm";
import { SeminarSection } from "@/components/marketing/SeminarSection";
import { AnimatedElement } from "@/components/marketing/AnimatedSection";
import {
  Band,
  BenefitCards,
  CtaButton,
  FaqGrid,
  FloatingCard,
  FormFootnote,
  FormSection,
  FounderSection,
  HeroCard,
  LandingHero,
  NoticeBox,
  Section,
  SectionHead,
  StepsGrid,
  TimelineRow,
  YesNo,
  useForcedLang,
} from "@/components/marketing/landing/LandingBlocks";
import { useLang } from "@/contexts/language-context";
import { formSectionCopy, type PartnerPerfil } from "@/lib/marketing/partnersLanding";

/**
 * /partners: una página, dos puertas (foodie y referidor). Las dos cobran la
 * misma tabla del programa de referidos (spec 2026-10-07, v4.6): $100 / $250 / $500 / $1,000
 * según las ventas con tarjeta del restaurante en su primer mes completo tras
 * activar. Las cifras viven SOLO aquí, con su aviso; nunca en un video ni en
 * un anuncio. (Los "30 días de Pro" para el referido se retiraron de la copy
 * el 2026-10-14: no existen términos de prueba publicados — ver PR de claims.)
 *
 * La línea (Samuel, 2026-10-09): el foodie y el referidor NO venden ni
 * "presentan a alguien". Conectan a un dueño que ya confía en ellos con un
 * producto ganador. El protagonista de la página es el producto (precio
 * dual, plan de software gratis con pagos de Redy), no Samuel ni ellos.
 * Nada que rebaje a la persona ("comes gratis", etc.).
 */
export default function PartnersClient({
  perfil: initial,
  forcedLang,
  founderPhoto,
  referralsPublicEnabled,
  seminarUrl,
}: {
  perfil: PartnerPerfil;
  forcedLang: "es" | "en" | null;
  founderPhoto: string | null;
  /** REFERRALS_PUBLIC_ENABLED: la puerta referidor es el registro propio del programa. */
  referralsPublicEnabled: boolean;
  /** Calendario GHL del seminario de socios; sin él la sección no se muestra. */
  seminarUrl?: string | null;
}) {
  const { t } = useLang();
  useForcedLang(forcedLang);
  const [perfil, setPerfil] = useState<PartnerPerfil>(initial);
  const esFoodie = perfil === "foodie";
  const registro = !esFoodie && referralsPublicEnabled;
  const formCopy = formSectionCopy(registro, t);
  const cta = t("Call me", "Quiero que me llamen");

  const tiers: { range: string; pay: string; note: string; featured?: boolean }[] = [
    { range: "$10,000 – $24,999", pay: "$100", note: t("in card sales, month 1", "en ventas con tarjeta, mes 1") },
    { range: "$25,000 – $49,999", pay: "$250", note: t("in card sales, month 1", "en ventas con tarjeta, mes 1"), featured: true },
    { range: "$50,000 – $99,999", pay: "$500", note: t("in card sales, month 1", "en ventas con tarjeta, mes 1") },
    { range: t("$100,000 or more", "$100,000 o más"), pay: "$1,000", note: t("in card sales, month 1", "en ventas con tarjeta, mes 1") },
  ];

  return (
    <>
      <MarketingNav />
      <main>
        <LandingHero
          eyebrow={t("Redy partners · Dallas", "Partners de Redy · Dallas")}
          title={esFoodie ? t("You know the owners.", "Conoces a los dueños.") : t("An owner trusts you.", "Un dueño confía en ti.")}
          accent={
            esFoodie
              ? t("There's a winning product for them.", "Hay un producto ganador para ellos.")
              : t("Connect them with a winning product.", "Conéctalo con un producto ganador.")
          }
          lead={
            esFoodie
              ? t(
                  "Restaurant owners already trust you. There's a system that works in their favor: dual pricing so card payers cover the processing cost, and a free software plan when they process payments with Redy. Connecting the two has a reward.",
                  "Los dueños de restaurante ya confían en ti. Hay un sistema que les conviene: precio dual para que quien paga con tarjeta cubra el costo de procesamiento, y un plan de software gratis si procesan sus pagos con Redy. Conectar a los dos tiene recompensa."
                )
              : t(
                  "Your brother-in-law, your client, the owner next door. They listen to you. There's a system where card payers cover the processing cost, with a free software plan when they process payments with Redy. You just make the connection, and you earn from it.",
                  "Tu cuñado, tu cliente, tu vecino de local. Ellos te escuchan. Hay un sistema donde quien paga con tarjeta cubre el costo de procesamiento, con un plan de software gratis si procesan sus pagos con Redy. Tú solo haces la conexión, y ganas con eso."
                )
          }
          cta={cta}
          secondary={{ label: t("See the product", "Ver el producto"), href: "#producto" }}
          trust={[
            { icon: Handshake, label: t("You don't sell. You connect.", "No vendes: conectas") },
            { icon: Wallet, label: t("PayPal or Venmo", "PayPal o Venmo") },
            { icon: ShieldCheck, label: t("No contract, no exclusivity", "Sin contrato ni exclusividad") },
          ]}
          visual={<PartnersVisual />}
        >
          {/* Selector de puerta */}
          <div className="mb-6">
            <div className="inline-flex rounded-full border border-black/5 bg-white p-1 shadow-sm" role="tablist">
              {(["foodie", "referidor"] as const).map((p) => (
                <button
                  key={p}
                  type="button"
                  role="tab"
                  aria-selected={perfil === p}
                  onClick={() => setPerfil(p)}
                  className={`px-5 py-2 rounded-full text-[13px] font-bold transition-all cursor-pointer ${
                    perfil === p ? "bg-redy-charcoal text-white shadow" : "text-redy-secondary hover:text-redy-charcoal"
                  }`}
                >
                  {p === "foodie" ? t("I'm a foodie", "Soy foodie") : t("I know owners", "Conozco dueños")}
                </button>
              ))}
            </div>
          </div>
        </LandingHero>

        <Band
          items={[
            { value: "$0", label: t("Start plan for the owner", "plan Start para el dueño"), sub: t("with Redy payments, month to month", "con pagos de Redy, mes a mes") },
            { value: "Dual", label: t("pricing built in", "precio dual incluido"), sub: t("card payers cover the processing cost", "quien paga con tarjeta cubre el procesamiento") },
            { value: "$249", label: t("one-time terminal", "terminal de pago único"), sub: t("the owner keeps it", "es del dueño") },
            { value: "90", label: t("days", "días"), sub: t("the connection is yours from the booking", "la conexión es tuya desde que agenda") },
          ]}
        />

        {/* El producto */}
        <Section id="producto">
          <SectionHead
            eyebrow={t("The product", "El producto")}
            title={t("What the owner gets.", "Lo que recibe el dueño.")}
            muted={t("That's why they say yes.", "Por eso dice que sí.")}
            lead={t(
              "What you connect has to be good for real. This is what the restaurant gets when it activates.",
              "Lo que conectas tiene que ser bueno de verdad. Esto es lo que recibe el restaurante cuando activa."
            )}
          />
          <BenefitCards
            items={[
              {
                icon: CreditCard,
                title: t("Card fees, covered", "Comisiones de tarjeta, cubiertas"),
                body: t("With dual pricing, the card price is designed to cover the processing cost, so it doesn't come out of the owner's margin.", "Con precio dual, el precio con tarjeta está pensado para cubrir el costo de procesamiento, así no sale del margen del dueño."),
              },
              {
                icon: BadgeDollarSign,
                title: t("$0 software plan", "Plan de software $0"),
                body: t("Free software plan when they process payments with Redy. Month to month, no long-term contract.", "Plan de software gratis si procesan sus pagos con Redy. Mes a mes, sin contrato a largo plazo."),
              },
              {
                icon: Wrench,
                title: t("Install and support included", "Instalación y soporte incluidos"),
                body: t("Redy installs, trains and stays. The owner is never left alone.", "Redy instala, enseña y acompaña. El dueño no se queda solo."),
              },
              {
                icon: Gift,
                title: t("Want to try Pro?", "¿Quiere probar Pro?"),
                body: t("Ask us about a trial.", "Pregúntanos por una prueba."),
              },
            ]}
          />
        </Section>

        {/* Cómo funciona */}
        <Section id="como" tone="soft">
          <SectionHead
            eyebrow={t("How it works", "Cómo funciona")}
            title={t("You make the connection.", "Tú haces la conexión.")}
            muted={t("We do the rest.", "Nosotros hacemos el resto.")}
          />
          <StepsGrid
            items={[
              {
                title: t("You connect the owner with the product", "Conectas al dueño con el producto"),
                body: t(
                  "With your link, or with their name and phone in the form. If they book their visit, the connection is yours for 90 days.",
                  "Con tu link o con su nombre y teléfono en el formulario. Si él agenda su visita, la conexión es tuya por 90 días."
                ),
              },
              {
                title: t("We do everything", "Nosotros hacemos todo"),
                body: t("The demo, the install, the support. You don't sell anything.", "La demo, la instalación, el soporte. Tú no vendes nada."),
              },
              {
                title: t("When they activate, you get paid", "Cuando activa, cobras"),
                body: t(
                  "We measure one full month of card sales and pay by PayPal or Venmo.",
                  "Se mide un mes completo de ventas con tarjeta y se paga por PayPal o Venmo."
                ),
              },
            ]}
          />
        </Section>

        {/* Cuánto */}
        <Section>
          <SectionHead
            eyebrow={t("How much", "Cuánto")}
            title={t("The bigger the restaurant,", "Mientras más vende el restaurante,")}
            muted={t("the bigger your check.", "más grande tu cheque.")}
            center
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-[1100px] mx-auto items-stretch">
            {tiers.map((tier, i) => (
              <AnimatedElement
                key={tier.pay}
                delay={0.08 * i}
                className={`relative rounded-[28px] p-7 lg:p-6 xl:p-7 text-center overflow-hidden ${
                  tier.featured
                    ? "bg-[#111] text-white shadow-[0_32px_80px_rgba(0,0,0,0.25)] md:-translate-y-3"
                    : "bg-white border border-black/5 shadow-sm"
                }`}
              >
                {tier.featured ? (
                  <>
                    <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: "linear-gradient(90deg,#D7263D,#F4722B)" }} />
                    <div className="absolute -top-16 -right-16 w-48 h-48 bg-redy-orange/20 rounded-full blur-[60px] pointer-events-none" />
                  </>
                ) : null}
                <div className={`relative text-[11px] font-bold tracking-widest uppercase mb-5 ${tier.featured ? "text-white/50" : "text-redy-secondary"}`}>
                  {tier.range}
                </div>
                <div
                  className={`relative text-[60px] md:text-[64px] lg:text-[48px] xl:text-[56px] font-black tracking-tight leading-none ${tier.featured ? "redy-text-gradient" : "text-redy-charcoal"}`}
                >
                  {tier.pay}
                </div>
                <div className={`relative text-[13px] font-medium mt-4 ${tier.featured ? "text-white/55" : "text-redy-secondary"}`}>{tier.note}</div>
              </AnimatedElement>
            ))}
          </div>
          <div className="max-w-[1100px] mx-auto">
            <NoticeBox title={t("The fine print, in plain sight:", "La letra chica, a la vista:")}>
              {t(
                "Under $10,000 doesn't pay that month; it's measured again the next. Payment depends on the restaurant actually activating (its first day with 10 orders or $500 on cards). There are no payments for signing up or for bringing other people into the program.",
                "Menos de $10,000 no paga ese mes; se vuelve a medir el siguiente. El pago depende de que el restaurante se active de verdad (su primer día con 10 órdenes o $500 con tarjeta). No hay pagos por registrarte ni por traer a otras personas al programa."
              )}
            </NoticeBox>
          </div>
        </Section>

        {/* Qué ganas */}
        <Section tone="soft">
          <SectionHead
            eyebrow={t("What you get", "Lo que ganas")}
            title={t("One connection.", "Una conexión.")}
            muted={t("Both sides win.", "Ganan los dos.")}
            lead={t(
              "The owner gets a better system. You get paid for the connection. Nobody loses.",
              "El dueño queda con un sistema mejor. Tú cobras por la conexión. Nadie pierde."
            )}
          />
          <BenefitCards
            items={
              esFoodie
                ? [
                    { icon: Handshake, title: t("You connect, you don't sell", "Conectas, no vendes"), body: t("The owner already listens to you. The product does the convincing.", "El dueño ya te escucha. El producto se encarga de convencer.") },
                    { icon: ShieldCheck, title: t("Your name stays good", "Tu nombre queda bien"), body: t("What you connect works. The owner ends up better than before.", "Lo que conectas funciona. El dueño queda mejor que antes.") },
                    { icon: Link2, title: t("Your own link", "Tu propio link"), body: t("So the credit is yours, not someone else's.", "Para que el crédito sea tuyo y de nadie más.") },
                    { icon: Utensils, title: t("Partner restaurants, as always", "Los aliados, como siempre"), body: t("You keep collaborating with whoever you want.", "Sigues colaborando con quien quieras.") },
                  ]
                : [
                    { icon: Gift, title: t("A free plan to start", "Un plan gratis para empezar"), body: t("The owner you connect can start on the free Start plan when they process payments with Redy.", "El dueño que conectas puede empezar con el plan Start gratis si procesa sus pagos con Redy.") },
                    { icon: Wallet, title: t("You get paid on activation", "Cobras cuando activa"), body: t("Without selling or installing anything.", "Sin vender ni instalar nada.") },
                    { icon: Link2, title: t("Your own link", "Tu propio link"), body: t("So the credit is yours, not someone else's.", "Para que el crédito sea tuyo y de nadie más.") },
                    { icon: Users, title: t("Works for anyone who knows owners", "Sirve a quien conoce dueños"), body: t("Accountants, suppliers, other owners.", "Contadores, proveedores, otros dueños.") },
                  ]
            }
          />
        </Section>

        {/* Qué es y qué no */}
        <Section>
          <SectionHead
            eyebrow={t("Straight talk", "Hablando claro")}
            title={t("What this is.", "Qué es esto.")}
            muted={t("And what it isn't.", "Y qué no es.")}
          />
          <YesNo
            yesTitle={t("What it is", "Qué sí es")}
            yes={[
              t("A connection: an owner who trusts you and a product that works for them.", "Una conexión: un dueño que confía en ti y un producto que le conviene."),
              t("A payment for every restaurant that actually activates.", "Un pago por cada restaurante que de verdad se activa."),
              t("Your link and your name on every connection, for 90 days.", "Tu link y tu nombre en cada conexión, por 90 días."),
              t("A simple referral agreement you read before accepting.", "Un acuerdo simple de referidos que lees antes de aceptar."),
            ]}
            noTitle={t("What it is NOT", "Qué NO es")}
            no={[
              t("Selling. You connect; the demo and the install are on Redy.", "Vender. Tú conectas; la demo y la instalación las hace Redy."),
              t("A pyramid. You earn for active restaurants, not for people.", "Pirámide. Cobras por restaurantes activos, no por gente."),
              t("Exclusive. Keep collaborating with whoever you want.", "Exclusivo. Puedes seguir colaborando con quien quieras."),
              ...(esFoodie
                ? [t("Hidden. Every collaboration is disclosed in the video. It's the law, and we handle it together.", "Escondido. Toda colaboración se divulga en el video. Es la ley y lo cuidamos juntos.")]
                : [t("A commitment. No quotas, no minimums, no deadlines.", "Un compromiso. Sin cuotas, sin mínimos, sin plazos.")]),
            ]}
          />
        </Section>

        <FounderSection
          photo={founderPhoto}
          eyebrow={t("Who's behind the product", "Quién está detrás del producto")}
          quote={t(
            "I built Redy because nothing out there worked for a Latino restaurant. It runs every day in my own kitchen before it reaches anyone else's.",
            "Construí Redy porque nada de lo que había servía para un restaurante latino. Corre todos los días en mi propia cocina antes de llegar a la de nadie más."
          )}
          extraLine={t(
            "Eight years at Marriott, from busboy to F&B manager. Owner of Empanada Factory in Dallas. The restaurants you connect get the same system I use.",
            "Ocho años en Marriott, de cargar platos a gerente de alimentos y bebidas. Dueño de Empanada Factory en Dallas. Los restaurantes que conectas reciben el mismo sistema que yo uso."
          )}
        />

        {/* FAQ */}
        <Section tone="soft">
          <SectionHead eyebrow="FAQ" title={t("Questions we", "Preguntas que")} muted={t("get a lot", "nos hacen seguido")} center />
          <FaqGrid
            items={[
              {
                q: t("Do I have to convince the owner?", "¿Tengo que convencer al dueño?"),
                a: t(
                  "No. You connect; Redy does the demo. If it doesn't work for them, nothing happens: no commitment for them or for you.",
                  "No. Tú conectas; la demo la hace Redy. Si no le conviene, no pasa nada: no hay compromiso ni para él ni para ti."
                ),
              },
              { q: t("When do I get paid?", "¿Cuándo me pagan?"), a: t("When the first full month that qualifies closes.", "Al cerrar el primer mes completo que califica.") },
              { q: t("What if the restaurant already knew Redy?", "¿Y si el restaurante ya conocía Redy?"), a: t("The one who books the visit first gets the credit.", "Cuenta el que agenda la cita primero.") },
              { q: t("Do I need a big following?", "¿Necesito muchos seguidores?"), a: t("No. You need to know owners.", "No. Necesitas conocer dueños.") },
            ]}
          />
          <div className="mt-14 flex justify-center">
            <CtaButton label={cta} />
          </div>
        </Section>

        {/* Seminario quincenal: va junto al formulario, que sigue siendo la otra puerta. */}
        <SeminarSection audience="partners" calendarUrl={seminarUrl} />

        <FormSection
          eyebrow={t("Next step", "Siguiente paso")}
          title={formCopy.title}
          lead={formCopy.lead}
          points={formCopy.points}
          footnote={
            <FormFootnote>
              {t(
                "By submitting you agree to a call and to text messages about this program. Terms:",
                "Al enviar aceptas que te llamemos y te escribamos por mensaje de texto sobre este programa. Términos de"
              )}
            </FormFootnote>
          }
        >
          <PartnerLeadForm key={perfil} door={perfil} referralsPublicEnabled={referralsPublicEnabled} />
        </FormSection>
      </main>
      <MarketingFooter />
    </>
  );
}

/** Visual del hero: la conexión (dueño → producto) y lo que pasa después, como se vería en el panel. */
function PartnersVisual() {
  const { t } = useLang();
  return (
    <div className="relative">
      <HeroCard kicker={t("The connection · example", "La conexión · ejemplo")}>
        <div className="mt-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-[14px] bg-white/[0.06] border border-white/10 grid place-items-center shrink-0">
              <Store className="w-5 h-5 text-redy-orange" />
            </div>
            <div className="min-w-0">
              <p className="text-[14px] font-bold text-white truncate">{t("Taquería (Oak Cliff)", "Taquería (Oak Cliff)")}</p>
              <p className="text-[11px] text-white/45">{t("The owner trusts you", "El dueño confía en ti")}</p>
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-redy-green/15 text-redy-green text-[10px] font-black uppercase tracking-wider shrink-0">
            <span className="w-1.5 h-1.5 rounded-full bg-redy-green" />
            {t("Active", "Activo")}
          </span>
        </div>

        <div className="mt-6">
          <TimelineRow
            done
            title={t("You connected them with the product", "Lo conectaste con el producto")}
            sub={t("$0/month plan · dual pricing built in", "Plan de $0 al mes · precio dual incluido")}
          />
          <TimelineRow done title={t("Redy installed it", "Redy lo instaló")} sub={t("Demo, install, support: not your job", "Demo, instalación, soporte: no es tu trabajo")} />
          <TimelineRow
            done={false}
            last
            title={t("First full month closed", "Primer mes completo cerrado")}
            sub={t("Card sales: $31,400", "Ventas con tarjeta: $31,400")}
          />
        </div>

        <div className="mt-6 pt-5 border-t border-white/10 flex items-end justify-between">
          <div>
            <p className="text-[10px] font-bold tracking-[0.18em] uppercase text-white/40">{t("You get paid", "Cobras")}</p>
            <p className="text-[44px] font-black tracking-tight leading-none mt-1 redy-text-gradient">$250</p>
          </div>
          <p className="text-[11px] text-white/45 font-medium text-right leading-snug">
            PayPal
            <br />
            {t("or Venmo", "o Venmo")}
          </p>
        </div>
      </HeroCard>

      <FloatingCard from="left" delay={0.9} className="-left-[200px] top-[16%]">
        <div className="w-10 h-10 bg-redy-green/10 rounded-full flex items-center justify-center shrink-0">
          <Gift className="w-[18px] h-[18px] text-redy-green" />
        </div>
        <div>
          <div className="text-[12px] font-black text-redy-charcoal leading-tight">{t("Start plan, $0", "Plan Start, $0")}</div>
          <div className="text-[10px] font-bold text-redy-secondary">{t("For the owner, with Redy payments.", "Para el dueño, con pagos de Redy.")}</div>
        </div>
      </FloatingCard>

      <FloatingCard from="right" delay={1.1} className="-right-[150px] -bottom-8">
        <div className="w-10 h-10 bg-redy-orange/10 rounded-full flex items-center justify-center shrink-0">
          <Link2 className="w-[18px] h-[18px] text-redy-orange" />
        </div>
        <div>
          <div className="text-[12px] font-black text-redy-charcoal leading-tight">{t("useredy.com/ref/YOURCODE", "useredy.com/ref/TUCÓDIGO")}</div>
          <div className="text-[10px] font-bold text-redy-secondary">{t("Your link. Your credit.", "Tu link. Tu crédito.")}</div>
        </div>
      </FloatingCard>
    </div>
  );
}
